// ── 테넌트 관리자 계정 패널 (테넌트 표 행 아래 인라인) ──────────────────────────
// 한 테넌트의 관리자(TENANT_ADMIN) 목록 조회 + 비밀번호 초기화 + 새 관리자 생성.
//   - 운영사(isOperator)만 생성/초기화 버튼 노출. 테넌트 관리자는 조회만(백엔드 operatorOnly 이중 방어).
//   - 평문 비번은 화면에 절대 표시하지 않는다(운영사가 입력한 값이므로 이미 앎).
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

const input = { padding: '6px 9px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, background: 'var(--bg-card)', color: 'var(--text)' };
const btn = { padding: '6px 12px', border: '1px solid var(--std-btn, #0f172a)', borderRadius: 7, background: 'var(--std-btn, #0f172a)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
const btnOutline = { padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg-card)', color: 'var(--text)', fontSize: 12, cursor: 'pointer' };
const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--bg-subtle)' };

function activeBadge(on) {
  const c = on ? '#16a34a' : '#6b7280';
  return <span style={{ fontSize: 11, fontWeight: 800, color: c, border: `1px solid ${c}`, borderRadius: 5, padding: '1px 7px' }}>{on ? '사용' : '비활성'}</span>;
}

export default function TenantAdminPanel({ tenantCode, isOperator, showToast }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetId, setResetId] = useState(null);   // 비번초기화 입력이 열린 관리자 id
  const [resetPw, setResetPw] = useState('');
  const [form, setForm] = useState({ loginId: '', name: '', password: '' });

  const load = useCallback(async () => {
    if (!tenantCode) return;
    setLoading(true);
    const res = await apiClient.get(`/api/admin/saas/tenants/${tenantCode}/admins`);
    setLoading(false);
    if (res.ok) setAdmins(res.data?.data || []);
    else showToast(res.data?.message || '관리자 목록을 불러오지 못했습니다.', 'error');
  }, [tenantCode, showToast]);
  useEffect(() => { load(); }, [load]);

  async function submitReset(row) {
    if ((resetPw || '').length < 4) { showToast('임시 비밀번호(4자 이상)를 입력하세요.', 'error'); return; }
    const res = await apiClient.post(`/api/admin/saas/tenants/${tenantCode}/admins/${row.id}/reset-password`, { password: resetPw });
    if (!res.ok) { showToast(res.data?.message || '비밀번호 초기화에 실패했습니다.', 'error'); return; }
    showToast(`${row.name}(${row.login_id})의 비밀번호를 초기화했습니다. 첫 로그인 시 변경됩니다.`, 'success');
    setResetId(null); setResetPw('');
    await load();
  }

  async function createAdmin() {
    const loginId = form.loginId.trim();
    if (!loginId || !form.name.trim()) { showToast('아이디와 이름을 입력하세요.', 'error'); return; }
    if ((form.password || '').length < 4) { showToast('임시 비밀번호(4자 이상)를 입력하세요.', 'error'); return; }
    const res = await apiClient.post(`/api/admin/saas/tenants/${tenantCode}/admins`, { loginId, name: form.name, password: form.password });
    if (!res.ok) { showToast(res.data?.message || '관리자 생성에 실패했습니다.', 'error'); return; }
    showToast(`테넌트 관리자 ${form.name}(${loginId})를 만들었습니다. 아이디·비번을 고객에게 전달하세요.`, 'success');
    setForm({ loginId: '', name: '', password: '' });
    await load();
  }

  return (
    <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <strong style={{ fontSize: 13 }}>{tenantCode} 관리자 계정</strong>

      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)', borderRadius: 7 }}>
        <thead><tr><th style={th}>아이디</th><th style={th}>이름</th><th style={th}>소속회사</th><th style={th}>상태</th><th style={th}>비번</th>{isOperator && <th style={th}>관리</th>}</tr></thead>
        <tbody>
          {loading && <tr><td style={td} colSpan={isOperator ? 6 : 5}>불러오는 중…</td></tr>}
          {!loading && admins.length === 0 && <tr><td style={td} colSpan={isOperator ? 6 : 5}>관리자 계정이 없습니다. {isOperator ? '아래에서 만들 수 있습니다.' : ''}</td></tr>}
          {!loading && admins.map((a) => (
            <React.Fragment key={a.id}>
              <tr>
                <td style={td}><strong>{a.login_id}</strong></td>
                <td style={td}>{a.name}</td>
                <td style={td}>{a.company_code}</td>
                <td style={td}>{activeBadge(a.is_active)}</td>
                <td style={td}>{a.must_change_password ? <span style={{ fontSize: 11, color: '#d97706' }}>변경대기</span> : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>설정됨</span>}</td>
                {isOperator && (
                  <td style={td}>
                    {resetId === a.id
                      ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>아래 입력</span>
                      : <button style={btnOutline} onClick={() => { setResetId(a.id); setResetPw(''); }}>비번초기화</button>}
                  </td>
                )}
              </tr>
              {isOperator && resetId === a.id && (
                <tr>
                  <td style={td} colSpan={6}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.login_id} 새 임시비번:</span>
                      <input type="password" style={{ ...input, width: 160 }} placeholder="임시비번(4자+)" value={resetPw} onChange={(e) => setResetPw(e.target.value)} />
                      <button style={btn} onClick={() => submitReset(a)}>초기화</button>
                      <button style={btnOutline} onClick={() => { setResetId(null); setResetPw(''); }}>취소</button>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>첫 로그인 시 변경 강제. 비번은 화면에 저장·표시되지 않습니다.</span>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {isOperator && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px dashed var(--border)', paddingTop: 10 }}>
          <strong style={{ fontSize: 12 }}>새 관리자 만들기</strong>
          <input style={{ ...input, width: 150 }} placeholder="로그인 아이디" value={form.loginId} onChange={(e) => setForm({ ...form, loginId: e.target.value })} />
          <input style={{ ...input, width: 120 }} placeholder="이름" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input type="password" style={{ ...input, width: 140 }} placeholder="임시비번(4자+)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <button style={btn} onClick={createAdmin}>만들기</button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>이 테넌트만 관리(자기 테넌트 외 접근 불가). 임시비번은 직접 입력, 첫 로그인 시 변경 강제.</span>
        </div>
      )}
    </div>
  );
}
